import { useCallback } from 'react';

import { Event } from 'models';
import { UserHelpers } from 'helpers/UserHelpers';

import { useEvents } from 'hooks/use-events';
import { ExpiringLocalStorageData, useExpiringLocalStorage } from 'hooks/use-expiring-local-storage';
import { EventService } from 'services/EventService';

export type ExpiringLocalEvent = ExpiringLocalStorageData<Event['id'] | null>;

const useLocalEvent = () => {
    const { events, isLoading } = useEvents();

    const defaultTimeToLive = 24 * 60 * 60 * 1000; // 24 hours

    const [localEventId, setLocalEvent] = useExpiringLocalStorage<Event['id'] | null>({
        key: UserHelpers.localStorageKey.event,
        defaultValue: null,
        timeToLive: defaultTimeToLive,
    });

    const localEvent =
        events.find((event) => {
            return event.id === localEventId;
        }) || null;

    const setLocalEventWithExpiry = useCallback(
        (eventId: Event['id'] | null) => {
            if (!eventId) {
                setLocalEvent(null);
                return;
            }

            const event = events.find((e) => e.id === eventId);

            if (!event) return;

            const now = Date.now();
            const eventEnd = new Date(event.end).getTime();

            const isFutureEvent = eventEnd >= now;
            const ttl = isFutureEvent ? eventEnd - now + defaultTimeToLive : defaultTimeToLive;

            setLocalEvent(eventId, ttl);
            EventService.dismissEventSuggestion(eventId);
        },
        [events, setLocalEvent, defaultTimeToLive],
    );

    const clear = () => {
        setLocalEvent(null);
    };

    return {
        localEvent,
        isLoading,
        setLocalEvent: setLocalEventWithExpiry,
        clearLocalEvent: clear,
    };
};

export { useLocalEvent };
